/**
 * Datos de facturación del suscriptor — `D3-b` del informe
 * `AUDITORIA-DECISIONES-FISCALES-2026-10-08.md`, sobre la Decisión 3 de la
 * asesoría fiscal.
 *
 * ## Qué pide, y por qué cada campo está separado
 *
 * ```text
 * TERRITORIO   donde esta ESTABLECIDO. No es el pais del domicilio, y la
 *              etiqueta lo dice: confundirlos es el error que la Decision 3
 *              corrige -"un domicilio postal español no demuestra que una
 *              empresa este establecida"-
 * CONDICION    empresa/autonomo o particular
 * NIF          con su PAIS, que tampoco es el de establecimiento
 * DOMICILIO    va en la factura y no localiza nada
 * ```
 *
 * ## Lo que esta pantalla NO decide
 *
 * **Si con estos datos se puede contratar lo dice el servidor**, en
 * `puedeContratar`, y el motivo en `motivo`. Esta pantalla no sabe qué
 * territorios deja fuera `OD-0029` `D-01` y no debe aprenderlo: un segundo
 * criterio en el cliente es un criterio que se desincroniza del servidor en
 * silencio, y aquí lo que se desincronizaría es un tipo de IVA.
 *
 * Tampoco esconde el formulario a nadie por las capabilities: si la API
 * responde 403, se enseña "sin permiso" —el mismo criterio que
 * `PantallaEmisor` y `PantallaPlan`—.
 */
import { useCallback, useEffect, useState } from "react";
import {
  api,
  CONDICIONES_DESTINATARIO,
  TERRITORIOS_IVA,
  TIPOS_ID_SUSCRIPTOR,
  type CondicionDestinatario,
  type DatosFiscalesSuscriptor,
  type TerritorioIva,
  type TipoIdSuscriptor,
} from "../api/cliente";
import { t } from "../i18n/es";
import { Aviso, Boton, Campo, Selector, Tarjeta } from "../ui/primitivas";

const T = t.plan.datosFiscales;

/**
 * El formulario arranca **sin territorio y sin condición elegidos**.
 *
 * Es deliberado y es el centro de `D3-b`. Si arrancara en «Península y
 * Baleares» porque es lo más común, quien no lea la pregunta declararía un
 * establecimiento que no es el suyo, y el 21 % volvería a aplicarse solo —por
 * omisión del usuario esta vez, que es igual de malo—.
 */
const VACIO = {
  territorioIva: "" as TerritorioIva | "",
  condicion: "" as CondicionDestinatario | "",
  razonSocial: "",
  taxCountry: "ES",
  taxIdType: "NIF_ES_ENTITY" as TipoIdSuscriptor,
  taxIdValue: "",
  addressLine: "",
  postalCode: "",
  city: "",
  province: "",
  country: "ES",
};

type Borrador = typeof VACIO;

const SIN_ELEGIR = { valor: "" as const, texto: "—" };

const OPCIONES_TERRITORIO = [SIN_ELEGIR, ...TERRITORIOS_IVA.map((v) => ({ valor: v, texto: T.territorios[v] }))];
const OPCIONES_CONDICION = [SIN_ELEGIR, ...CONDICIONES_DESTINATARIO.map((v) => ({ valor: v, texto: T.condiciones[v] }))];
const OPCIONES_TIPO_ID = TIPOS_ID_SUSCRIPTOR.map((v) => ({ valor: v, texto: T.tipos[v] }));

function borradorDe(p: DatosFiscalesSuscriptor): Borrador {
  return {
    territorioIva: p.territorioIva,
    condicion: p.condicion,
    razonSocial: p.razonSocial,
    taxCountry: p.taxCountry,
    taxIdType: p.taxIdType,
    taxIdValue: p.taxIdValue,
    addressLine: p.addressLine,
    postalCode: p.postalCode,
    city: p.city,
    province: p.province ?? "",
    country: p.country,
  };
}

type Estado =
  | { readonly tipo: "cargando" }
  | { readonly tipo: "datos"; readonly perfil: DatosFiscalesSuscriptor | null; readonly motivo: string | null }
  | { readonly tipo: "error" }
  | { readonly tipo: "sin-permiso" };

export function TarjetaDatosFiscales({
  onCambio,
  yaContratado = false,
}: {
  onCambio: (puedeContratar: boolean) => void;
  /*
    Con una suscripcion viva el aviso «Rellena tus datos para poder contratar»
    sobra: ya ha contratado. Se le enseñaba a una cuenta Business al dia
    (captura del propietario, 2026-10-19). Solo se calla el AVISO; el gate del
    checkout sigue en la API y el formulario sigue ahi.
  */
  yaContratado?: boolean;
}) {
  const [estado, setEstado] = useState<Estado>({ tipo: "cargando" });
  const [borrador, setBorrador] = useState<Borrador>(VACIO);
  const [editando, setEditando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [campos, setCampos] = useState<Readonly<Record<string, string>>>({});
  const [fallo, setFallo] = useState<string | null>(null);
  const [guardado, setGuardado] = useState(false);

  const cargar = useCallback(async () => {
    const r = await api.datosFiscales();
    if (!r.ok) {
      setEstado({ tipo: r.estado === 403 ? "sin-permiso" : "error" });
      onCambio(false);
      return;
    }
    setEstado({ tipo: "datos", perfil: r.datos.perfil, motivo: r.datos.motivo });
    setBorrador(r.datos.perfil === null ? VACIO : borradorDe(r.datos.perfil));
    // Sin nada declarado, el formulario se abre solo: es el primer paso.
    setEditando(r.datos.perfil === null);
    onCambio(r.datos.puedeContratar);
  }, [onCambio]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  async function guardar() {
    setGuardando(true);
    setFallo(null);
    setCampos({});
    setGuardado(false);
    const r = await api.guardarDatosFiscales({
      ...borrador,
      // "" no es una opcion valida: la API lo rechaza con REQUIRED, y es lo
      // que se quiere. La pantalla no rellena el hueco.
      territorioIva: borrador.territorioIva as TerritorioIva,
      condicion: borrador.condicion as CondicionDestinatario,
      province: borrador.province.trim() === "" ? null : borrador.province,
    });
    setGuardando(false);
    if (!r.ok) {
      setCampos(r.error?.campos ?? {});
      setFallo(Object.keys(r.error?.campos ?? {}).length > 0 ? T.revisa : (r.error?.message ?? T.errorGuardar));
      return;
    }
    setGuardado(true);
    setEditando(false);
    await cargar();
  }

  if (estado.tipo === "cargando") {
    return <Tarjeta titulo={T.titulo}><p>{T.cargando}</p></Tarjeta>;
  }
  if (estado.tipo === "sin-permiso") {
    return <Tarjeta titulo={T.titulo}><Aviso tono="aviso">{t.plan.sinPermiso}</Aviso></Tarjeta>;
  }
  if (estado.tipo === "error") {
    return (
      <Tarjeta titulo={T.titulo}>
        <Aviso tono="error">{T.error}</Aviso>
        <Boton variante="secundario" onClick={() => void cargar()}>{t.plan.reintentar}</Boton>
      </Tarjeta>
    );
  }

  const motivo = estado.motivo;
  const aviso =
    motivo === null || yaContratado ? null : (T.motivos as Readonly<Record<string, string>>)[motivo] ?? T.noSePuedeContratar;

  return (
    <Tarjeta titulo={T.titulo}>
      <p className="ayuda-neutra">{T.intro}</p>

      {/* El motivo lo nombra el servidor; aqui solo se traduce. */}
      {aviso !== null ? <Aviso tono="aviso">{aviso}</Aviso> : null}
      {guardado && motivo === null ? <Aviso tono="exito">{T.guardado}</Aviso> : null}

      {!editando && estado.perfil !== null ? (
        <>
          <dl className="datos">
            <dt>{T.territorio}</dt>
            <dd>{T.territorios[estado.perfil.territorioIva]}</dd>
            <dt>{T.condicion}</dt>
            <dd>{T.condiciones[estado.perfil.condicion]}</dd>
            <dt>{T.razonSocial}</dt>
            <dd>{estado.perfil.razonSocial}</dd>
            <dt>{T.numeroId}</dt>
            <dd>{`${estado.perfil.taxCountry} · ${estado.perfil.taxIdValue}`}</dd>
            <dt>{T.direccion}</dt>
            <dd>
              {[estado.perfil.addressLine, estado.perfil.postalCode, estado.perfil.city, estado.perfil.province, estado.perfil.country]
                .filter((x) => x !== null && x !== "")
                .join(", ")}
            </dd>
          </dl>
          <div className="acciones">
            <Boton variante="secundario" onClick={() => setEditando(true)}>{T.editar}</Boton>
          </div>
        </>
      ) : (
        <>
          {/* El territorio arranca SIN elegir, a proposito. Ver VACIO. */}
          <Selector
            id="df-territorio"
            etiqueta={T.territorio}
            valor={borrador.territorioIva}
            opciones={OPCIONES_TERRITORIO}
            onChange={(v) => setBorrador({ ...borrador, territorioIva: v as TerritorioIva | "" })}
            error={campos.territorioIva ?? null}
          />
          <p className="ayuda-neutra">{T.territorioAyuda}</p>

          <Selector
            id="df-condicion"
            etiqueta={T.condicion}
            valor={borrador.condicion}
            opciones={OPCIONES_CONDICION}
            onChange={(v) => setBorrador({ ...borrador, condicion: v as CondicionDestinatario | "" })}
            error={campos.condicion ?? null}
          />

          <Campo
            id="df-razon"
            etiqueta={T.razonSocial}
            valor={borrador.razonSocial}
            onChange={(v) => setBorrador({ ...borrador, razonSocial: v })}
            error={campos.razonSocial ?? null}
          />

          <Selector
            id="df-tipo-id"
            etiqueta={T.tipoId}
            valor={borrador.taxIdType}
            opciones={OPCIONES_TIPO_ID}
            onChange={(v) => setBorrador({ ...borrador, taxIdType: v })}
            error={campos.taxIdType ?? null}
          />
          <Campo
            id="df-num-id"
            etiqueta={T.numeroId}
            valor={borrador.taxIdValue}
            onChange={(v) => setBorrador({ ...borrador, taxIdValue: v })}
            error={campos.taxIdValue ?? null}
          />
          <Campo
            id="df-pais-id"
            etiqueta={T.paisId}
            valor={borrador.taxCountry}
            onChange={(v) => setBorrador({ ...borrador, taxCountry: v.toUpperCase() })}
            ayuda={T.paisIdAyuda}
            error={campos.taxCountry ?? null}
          />

          <Campo
            id="df-direccion"
            etiqueta={T.direccion}
            valor={borrador.addressLine}
            onChange={(v) => setBorrador({ ...borrador, addressLine: v })}
            error={campos.addressLine ?? null}
          />
          <Campo
            id="df-cp"
            etiqueta={T.codigoPostal}
            valor={borrador.postalCode}
            onChange={(v) => setBorrador({ ...borrador, postalCode: v })}
            inputMode="numeric"
            error={campos.postalCode ?? null}
          />
          <Campo
            id="df-ciudad"
            etiqueta={T.ciudad}
            valor={borrador.city}
            onChange={(v) => setBorrador({ ...borrador, city: v })}
            error={campos.city ?? null}
          />
          <Campo
            id="df-provincia"
            etiqueta={T.provincia}
            valor={borrador.province}
            onChange={(v) => setBorrador({ ...borrador, province: v })}
            requerido={false}
            error={campos.province ?? null}
          />
          <Campo
            id="df-pais"
            etiqueta={T.pais}
            valor={borrador.country}
            onChange={(v) => setBorrador({ ...borrador, country: v.toUpperCase() })}
            error={campos.country ?? null}
          />

          {fallo !== null ? <Aviso tono="error">{fallo}</Aviso> : null}
          <div className="acciones">
            <Boton onClick={() => void guardar()} deshabilitado={guardando}>
              {guardando ? T.guardando : T.guardar}
            </Boton>
          </div>
        </>
      )}
    </Tarjeta>
  );
}
